import { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { rqx } from "../../../../core/request/API";
import { env } from "../../../../core/Env";
import { ToolContext } from "../../../../core/context/ToolContext";
import { Box, Button, Card, Chip, Divider, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from "@mui/material"
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { NoDataFound } from "../../empty/Index";
import { SnakeLoader } from "../../../../core/loader/SnakeLoader";
import { fetchUserAuthDetails } from "../socialMediaInsight/helpers/metricHelpers";

export const FacebookPosts = () => {
    const { tool_state } = useContext(ToolContext);
    const { userId, __SESSION } = tool_state;
    const history = useHistory();

    const [metrics, setMetrics] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [facebookPageId, setFacebookPageId] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        postId: null,
        postTitle: null,
        error: null,
        isDeleting: false
    });

    const [repostDialog, setRepostDialog] = useState({
        open: false,
        postId: null,
        postTitle: null,
        error: null,
        isReposting: false
    });

    const [failureDetailsDialog, setFailureDetailsDialog] = useState({
        open: false,
        postTitle: null,
        failureReason: null,
        attemptedAt: null
    });

    // Fetch insights for a specific post
    const fetchPostInsights = async (postId, token) => {
        try {
            const response = await rqx.fbGraph(
                `${postId}?fields=likes.summary(true).limit(0),comments.summary(true).limit(0),shares,insights.metric(post_impressions_unique)`,
                token
            );

            const insights = {
                likes: response?.likes?.summary?.total_count || 0,
                comments: response?.comments?.summary?.total_count || 0,
                shares: response?.shares?.count || 0,
                reach: response?.insights?.data?.[0]?.values?.[0]?.value || 0
            };

            return insights;
        } catch (error) {
            return {
                likes: 0,
                comments: 0,
                shares: 0,
                reach: 0
            };
        }
    };

    // Open delete confirmation dialog
    const openDeleteDialog = (postId, postTitle) => {
        setDeleteDialog({
            open: true,
            postId: postId,
            postTitle: postTitle,
            error: null,
            isDeleting: false
        });
    };

    // Close delete dialog
    const closeDeleteDialog = () => {
        setDeleteDialog({
            open: false,
            postId: null,
            postTitle: null,
            error: null,
            isDeleting: false
        });
    };

    // Close repost dialog
    const closeRepostDialog = () => {
        setRepostDialog({
            open: false,
            postId: null,
            postTitle: null,
            error: null,
            isReposting: false
        });
    };

    // Close failure details dialog
    const closeFailureDetailsDialog = () => {
        setFailureDetailsDialog({
            open: false,
            postTitle: null,
            failureReason: null,
            attemptedAt: null
        });
    };

    // Confirm repost handler
    const confirmRepost = async () => {
        setRepostDialog(prev => ({ ...prev, isReposting: true, error: null }));
    
        try {
            const response = await rqx.post(
                `<<tool>>/${env()}/autoPostToFacebook/publish`,
                {
                    social_post_id: repostDialog.postId,
                    created_by: userId.data,
                    user_email: __SESSION.data.email,
                    repost_mode: true
                }
            );
    
            if (response?.message === 'error') {
                setRepostDialog(prev => ({
                    ...prev,
                    error: response?.error || 'Failed to repost',
                    isReposting: false
                }));
            } else {
                closeRepostDialog();
                fetchData();
            }
        } catch (error) {
            console.error("Error reposting:", error);
            const errorMessage = error?.response?.data?.error || error?.message || 'An unexpected error occurred while reposting';
            setRepostDialog(prev => ({
                ...prev,
                error: errorMessage,
                isReposting: false
            }));
        }
    };

    // Confirm delete post handler
    const confirmDeletePost = async () => {
        setDeleteDialog(prev => ({ ...prev, isDeleting: true, error: null }));

        try {
            const response = await rqx.post(
                `<<tool>>/${env()}/autoPostToFacebook/delete_facebook_post`,
                {
                    user_id: userId.data,
                    social_post_id: deleteDialog.postId
                }
            );

            if (response?.message === 'error') {
                setDeleteDialog(prev => ({
                    ...prev,
                    error: response?.error || 'Failed to delete post',
                    isDeleting: false
                }));
            } else {
                closeDeleteDialog();
                fetchData();
            }
        } catch (error) {
            console.error("Error deleting post:", error);
            const errorMessage = error?.response?.data?.error || error?.message || 'An unexpected error occurred while deleting the post';
            setDeleteDialog(prev => ({
                ...prev,
                error: errorMessage,
                isDeleting: false
            }));
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch basic metrics and posts list
            const metricsRes = await rqx.get(`<<tool>>/${env()}/autoPostToFacebook/get_posts_metrics`, { user_id: userId.data });
            setMetrics(metricsRes.data);

            const postsRes = await rqx.get(`<<tool>>/${env()}/autoPostToFacebook/get_posts_list`, { user_id: userId.data });
            const postsList = postsRes.data?.posts || [];

            // Fetch Facebook authentication
            const authData = await fetchUserAuthDetails(__SESSION.data.uaccess[0].company_id, userId.data, '1');
            
            // Store Facebook page ID for button navigation
            if (authData && authData.page_id) {
                setFacebookPageId(authData.page_id);
            }

            // If we have auth data and posts, fetch insights for each post
            if (authData && authData.access_token && postsList.length > 0) {
                const postsWithInsights = await Promise.all(
                    postsList.map(async (post, index) => {
                        if (post.facebook_post_id && post.status === 3) { // Only for published posts
                            const insights = await fetchPostInsights(post.facebook_post_id, authData.access_token);
                            return {
                                ...post,
                                likes: insights.likes,
                                comments: insights.comments,
                                shares: insights.shares,
                                reach: insights.reach
                            };
                        }
                        return post;
                    })
                );
                setPosts(postsWithInsights);
            } else {
                console.warn("Cannot fetch insights:", {
                    hasAuthData: !!authData,
                    hasAccessToken: !!authData?.access_token,
                    postsCount: postsList.length
                });
                setPosts(postsList);
            }
        } catch (error) {
            console.error("Error details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [userId.data]);

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    padding: "32px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <SnakeLoader size="1rem" bg="#11783C" distance="0.3rem" />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: "80vh", p: 4 }}>
            <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                {/* ===== METRICS ===== */}
                <Box sx={{
                    boxShadow: "0px 0px 4px 0px #0000001F",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px"
                }}>
                    <Typography
                        sx={{
                            fontSize: "20px",
                            fontWeight: 600,
                            mb: 3,
                            pb: 2,
                            borderBottom: "1px solid #C5C5C5",
                        }}
                    >
                        Facebook Posts Management
                    </Typography>

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Card sx={{ flex: 1, bgcolor: "#2FD072", color: "#fff", borderRadius: "10px", p: 2 }}>
                            <Typography fontSize={14} fontWeight={500}>Total Posts</Typography>
                            <Typography fontSize={36} fontWeight={700}>
                                {metrics?.total_posts || 0}
                            </Typography>
                        </Card>

                        <Card sx={{ flex: 1, bgcolor: "#16AD8A", color: "#fff", borderRadius: "10px", p: 2 }}>
                            <Typography fontSize={14} fontWeight={500}>Published</Typography>
                            <Typography fontSize={36} fontWeight={700}>
                                {metrics?.published || 0}
                            </Typography>
                        </Card>

                        <Card sx={{ flex: 1, bgcolor: "#E85151", color: "#fff", borderRadius: "10px", p: 2 }}>
                            <Typography fontSize={14} fontWeight={500}>Failed</Typography>
                            <Typography fontSize={36} fontWeight={700}>
                                {metrics?.failed || 0}
                            </Typography>
                        </Card>

                        <Card sx={{ flex: 1, bgcolor: "#D4C557", color: "#fff", borderRadius: "10px", p: 2 }}>
                            <Typography fontSize={14} fontWeight={500}>Scheduled</Typography>
                            <Typography fontSize={36} fontWeight={700}>
                                {metrics?.scheduled || 0}
                            </Typography>
                        </Card>
                    </Box>
                </Box>

                {/* ===== POSTS LIST ===== */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {posts.length > 0 ? posts.map((post) => (
                        <Card
                            key={post.post_id}
                            sx={{
                                width: "100%",
                                boxShadow: "0px 0px 4px 0px #0000001F",
                                backgroundColor: "#FFFFFF",
                                borderRadius: "12px",
                                p: 3,
                            }}
                        >
                            {/* Header */}
                            <Box display="flex" alignItems="center" gap={2}>
                                <Box
                                    sx={{
                                        width: 70,
                                        height: 70,
                                        borderRadius: "11px",
                                        bgcolor: "#D9D9D9",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        overflow: "hidden"
                                    }}
                                >
                                    {post.post_image_s3_url ? (
                                        <img
                                            src={post.post_image_s3_url}
                                            alt="Post"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <Typography fontSize={10} color="#999">No Image</Typography>
                                    )}
                                </Box>

                                <Box flex={1}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontWeight={700}>
                                            {post.post_title}
                                        </Typography>

                                        <Chip
                                            label={post.status === 1 ? "Draft" : post.status === 2 ? "Scheduled" : post.status === 3 ? "Published" : post.status === 4 ? "Failed" : "Scheduled"}
                                            size="small"
                                            sx={{
                                                bgcolor:
                                                    post.status === 3
                                                        ? "#106C3647"
                                                        : "#FDD",
                                                color:
                                                    post.status === 3
                                                        ? "#106C36"
                                                        : "#C62828",
                                                fontWeight: 700,
                                                fontSize: 12,
                                            }}
                                        />
                                    </Box>

                                    <Typography fontSize={14} fontWeight={600} mt={1}>
                                        {post.post_message.slice(0, 280)}...
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 2,
                                            mt: 1,
                                            color: "#898989",
                                        }}
                                    >
                                        <Box display="flex" gap={0.5} alignItems="center">
                                            <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />
                                            <Typography fontSize={14}>
                                                {post.status === 3 
                                                    ? post.published_at 
                                                    : post.status === 4 
                                                        ? (post.updated_at || post.created_at || 'N/A')
                                                        : post.published_at || post.created_at || 'N/A'
                                                }
                                            </Typography>
                                        </Box>

                                        <Box display="flex" gap={0.5} alignItems="center">
                                            <Inventory2OutlinedIcon sx={{ fontSize: 14 }} />
                                            <Typography fontSize={14}>
                                                Product: {post.package_name}
                                            </Typography>
                                        </Box>

                                        <Box display="flex" gap={0.5} alignItems="center">
                                            <CampaignOutlinedIcon sx={{ fontSize: 18 }} />
                                            <Typography fontSize={14}>
                                                Promo: {post.promo_name}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Stats */}
                            <Box display="flex" gap={3}>
                                <Stat icon={<ThumbUpAltOutlinedIcon sx={{ color: "#1877F2" }} />} color="#1877F2" value={post.likes || 0} label="Likes" />
                                <Stat icon={<ChatBubbleOutlineOutlinedIcon sx={{ color: "#42B72A" }} />} color="#42B72A" value={post.comments || 0} label="Comments" />
                                <Stat icon={<ShareOutlinedIcon sx={{ color: "#F7B928" }} />} color="#F7B928" value={post.shares || 0} label="Shares" />
                                <Stat icon={<VisibilityOutlinedIcon sx={{ color: "#2E89A3" }} />} color="#2E89A3" value={post.reach || 0} label="Reach" />
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Actions */}
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="contained"
                                    sx={{ bgcolor: "#0098F6" }}
                                    onClick={() => {
                                        if (facebookPageId) {
                                            window.open(`https://www.facebook.com/${facebookPageId}`, "_blank");
                                        }
                                    }}
                                    disabled={!facebookPageId}
                                >
                                    View on Facebook
                                </Button>

                                <Button 
                                    variant="contained" 
                                    sx={{ bgcolor: "#7993B1" }}
                                    onClick={() => history.push('/reports/social-media-insight?state=facebook')}
                                >
                                    Analytics
                                </Button>

                                <Button 
                                    variant="contained" 
                                    sx={{ bgcolor: "#F06948" }}
                                    onClick={() => setRepostDialog({
                                        open: true,
                                        postId: post.id,
                                        postTitle: post.promo_name,
                                        error: null,
                                        isReposting: false
                                    })}
                                >
                                    {post.status !== 4 ? "Repost" : "Retry Publish"}
                                </Button>

                                {post.status === 4 ? (
                                    <Button
                                        variant="contained"
                                        sx={{ bgcolor: "#FFA726" }}
                                        onClick={() => setFailureDetailsDialog({
                                            open: true,
                                            postTitle: post.promo_name,
                                            failureReason: post.failure_reason,
                                            attemptedAt: post.updated_at || post.created_at
                                        })}
                                    >
                                        Failure Details
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        sx={{ bgcolor: "#FF6868" }}
                                        onClick={() => openDeleteDialog(post.id, post.promo_name)}
                                    >
                                        Delete
                                    </Button>
                                )}
                            </Box>
                        </Card>
                    )) : (
                        <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
                            <NoDataFound />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={!deleteDialog.isDeleting ? closeDeleteDialog : undefined}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        boxShadow: "0px 0px 4px 0px #0000001F",
                        p: "1.5rem"
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        pb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                    }}
                >
                    <WarningAmberIcon sx={{ color: "#FF6868", fontSize: 28 }} />
                    Delete Post
                </DialogTitle>

                <DialogContent>
                    <Typography sx={{ fontSize: "16px", color: "#333", mb: 2 }}>
                        Are you sure you want to delete this post?
                    </Typography>

                    {deleteDialog.postTitle && (
                        <Box
                            sx={{
                                bgcolor: "#F8FAFC",
                                borderRadius: "8px",
                                p: 2,
                                mb: 2,
                                border: "1px solid #E0E0E0"
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#555"
                                }}
                            >
                                {deleteDialog.postTitle}
                            </Typography>
                        </Box>
                    )}

                    <Typography sx={{ fontSize: "14px", color: "#666" }}>
                        This action cannot be undone. The post will be removed from Facebook.
                    </Typography>

                    {deleteDialog.error && (
                        <Alert
                            severity="error"
                            sx={{
                                mt: 2,
                                borderRadius: "8px"
                            }}
                        >
                            {deleteDialog.error}
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 6 }}>
                    <Button
                        onClick={closeDeleteDialog}
                        disabled={deleteDialog.isDeleting}
                        sx={{
                            color: "#666",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeletePost}
                        disabled={deleteDialog.isDeleting}
                        variant="contained"
                        sx={{
                            bgcolor: "#FF6868",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600,
                            "&:hover": {
                                bgcolor: "#E85151"
                            }
                        }}
                    >
                        {deleteDialog.isDeleting ? "Deleting..." : "Delete Post"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Repost Confirmation Dialog */}
            <Dialog
                open={repostDialog.open}
                onClose={!repostDialog.isReposting ? closeRepostDialog : undefined}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        boxShadow: "0px 0px 4px 0px #0000001F",
                        p: "1.5rem"
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        pb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                    }}
                >
                    <ShareOutlinedIcon sx={{ color: "#F06948", fontSize: 28 }} />
                    Repost to Facebook
                </DialogTitle>

                <DialogContent>
                    <Typography sx={{ fontSize: "16px", color: "#333", mb: 2 }}>
                        Are you sure you want to repost this to Facebook?
                    </Typography>

                    {repostDialog.postTitle && (
                        <Box
                            sx={{
                                bgcolor: "#F8FAFC",
                                borderRadius: "8px",
                                p: 2,
                                mb: 2,
                                border: "1px solid #E0E0E0"
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#555"
                                }}
                            >
                                {repostDialog.postTitle}
                            </Typography>
                        </Box>
                    )}

                    <Typography sx={{ fontSize: "14px", color: "#666", mb: 2 }}>
                        This will create a new post on Facebook with the same content. The original post will remain unchanged.
                    </Typography>

                    {repostDialog.error && (
                        <Alert
                            severity="error"
                            sx={{
                                mt: 2,
                                borderRadius: "8px"
                            }}
                        >
                            {repostDialog.error}
                        </Alert>
                    )}

                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 6 }}>
                    <Button
                        onClick={closeRepostDialog}
                        disabled={repostDialog.isReposting}
                        sx={{
                            color: "#666",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmRepost}
                        disabled={repostDialog.isReposting}
                        variant="contained"
                        sx={{
                            bgcolor: "#F06948",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600,
                            "&:hover": {
                                bgcolor: "#E05838"
                            }
                        }}
                    >
                        {repostDialog.isReposting ? "Reposting..." : "Repost"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Failure Details Dialog */}
            <Dialog
                open={failureDetailsDialog.open}
                onClose={closeFailureDetailsDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        boxShadow: "0px 0px 4px 0px #0000001F",
                        p: "1.5rem"
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        pb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                    }}
                >
                    <WarningAmberIcon sx={{ color: "#FFA726", fontSize: 28 }} />
                    Post Failure Details
                </DialogTitle>

                <DialogContent>
                    {failureDetailsDialog.postTitle && (
                        <Box
                            sx={{
                                bgcolor: "#FFF3E0",
                                borderRadius: "8px",
                                p: 2,
                                mb: 3,
                                border: "1px solid #FFE0B2"
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#E65100",
                                    mb: 0.5
                                }}
                            >
                                Promo Name
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    color: "#555"
                                }}
                            >
                                {failureDetailsDialog.postTitle}
                            </Typography>
                        </Box>
                    )}

                    {failureDetailsDialog.attemptedAt && (
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#666",
                                    mb: 1
                                }}
                            >
                                Failed At
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    color: "#888",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1
                                }}
                            >
                                <CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
                                {failureDetailsDialog.attemptedAt}
                            </Typography>
                        </Box>
                    )}

                    <Box>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#666",
                                mb: 1
                            }}
                        >
                            Failure Reason
                        </Typography>
                        <Box
                            sx={{
                                bgcolor: "#F8FAFC",
                                borderRadius: "8px",
                                p: 2,
                                border: "1px solid #E0E0E0",
                                maxHeight: "300px",
                                overflowY: "auto"
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    color: "#333",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    lineHeight: 1.6
                                }}
                            >
                                {failureDetailsDialog.failureReason || "No failure reason available"}
                            </Typography>
                        </Box>
                    </Box>

                    <Alert 
                        severity="info" 
                        sx={{ 
                            mt: 3,
                            borderRadius: "8px"
                        }}
                    >
                        You can try to repost this content using the "Retry Publish" button, or edit the promo and publish again.
                    </Alert>
                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        onClick={closeFailureDetailsDialog}
                        variant="contained"
                        sx={{
                            bgcolor: "#FFA726",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600,
                            "&:hover": {
                                bgcolor: "#FB8C00"
                            }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const Stat = ({ icon, value, label, color }) => (
    <Box display="flex" alignItems="center" gap={0.5}>
        {icon}
        <Typography fontSize={13} fontWeight={800} color={color}>
            {value}
        </Typography>
        <Typography fontSize={13} fontWeight={800} color="#898989">
            {label}
        </Typography>
    </Box>
);

export default FacebookPosts;